/**
 * Security Utilities for Input Sanitization and Validation
 */

// HTML entity encoding map
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;'
};

/**
 * Sanitize string input to prevent XSS attacks
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') {
    return '';
  }

  // Remove any script tags
  let sanitized = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove event handlers
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');

  // Remove javascript: URLs
  sanitized = sanitized.replace(/javascript:/gi, '');

  // Remove data: URLs (can be used for XSS)
  sanitized = sanitized.replace(/data:/gi, '');

  // Encode HTML entities
  sanitized = sanitized.replace(/[&<>"'`=/]/g, char => HTML_ENTITIES[char] || char);

  // Trim whitespace
  sanitized = sanitized.trim();

  // Limit length to prevent DoS
  const MAX_LENGTH = 1000;
  if (sanitized.length > MAX_LENGTH) {
    sanitized = sanitized.substring(0, MAX_LENGTH);
  }

  return sanitized;
};

/**
 * Sanitize search query - allows alphanumeric, spaces, and common safe characters
 * @param {string} query - Search query
 * @returns {string} - Sanitized query
 */
export const sanitizeSearchQuery = (query) => {
  if (typeof query !== 'string') {
    return '';
  }

  // First apply general sanitization
  let sanitized = sanitizeInput(query);

  // Only allow alphanumeric, spaces, hyphens, underscores, and parentheses
  // This is appropriate for chemical names and CAS numbers
  sanitized = sanitized.replace(/[^a-zA-Z0-9\s\-_()]/g, '');

  return sanitized.trim().substring(0, 200);
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether email is valid
 */
export const isValidEmail = (email) => {
  if (typeof email !== 'string') return false;

  // RFC 5322 compliant email regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  return emailRegex.test(email) && email.length <= 254;
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {{ valid: boolean, errors: string[] }} - Validation result
 */
export const validatePassword = (password) => {
  const errors = [];

  if (typeof password !== 'string') {
    return { valid: false, errors: ['Password is required'] };
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (password.length > 128) {
    errors.push('Password must be less than 128 characters');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  // Check for common weak patterns
  const commonPatterns = [
    /^123/,
    /password/i,
    /qwerty/i,
    /abc123/i,
    /(.)\1{2,}/  // Repeated characters
  ];

  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      errors.push('Password contains a common weak pattern');
      break;
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Sanitize filter values
 * @param {object} filters - Filter object
 * @returns {object} - Sanitized filters
 */
export const sanitizeFilters = (filters) => {
  const sanitizedFilters = {};

  // Allowed filter keys and their valid values
  const allowedTypes = ['Surfactant', 'Solvent', 'Inorganic', 'Organic'];
  const allowedPurities = ['90', '95', '98', '99', '99.5', '99.9'];

  if (filters.type && allowedTypes.includes(filters.type)) {
    sanitizedFilters.type = filters.type;
  }

  if (filters.purity && allowedPurities.includes(filters.purity)) {
    sanitizedFilters.purity = filters.purity;
  }

  return sanitizedFilters;
};

/**
 * Parse price string safely
 * @param {string} priceString - Price string like "₹450/kg"
 * @returns {number} - Numeric price value or 0 if invalid
 */
export const parsePrice = (priceString) => {
  if (typeof priceString !== 'string') {
    return 0;
  }

  // Remove currency symbols and text, keep only numbers and decimal point
  const numericString = priceString.replace(/[^0-9.]/g, '');

  // Handle multiple decimal points (keep only first)
  const parts = numericString.split('.');
  const cleanNumber = parts.length > 1
    ? `${parts[0]}.${parts.slice(1).join('')}`
    : numericString;

  const price = parseFloat(cleanNumber);

  // Validate the result
  if (isNaN(price) || price < 0 || price > Number.MAX_SAFE_INTEGER) {
    return 0;
  }

  return price;
};

/**
 * Safe localStorage operations with error handling
 */
export const safeStorage = {
  getItem: (key) => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;

      // Validate JSON before parsing
      const parsed = JSON.parse(item);
      return parsed;
    } catch (error) {
      console.error(`Error reading from localStorage: ${key}`, error);
      return null;
    }
  },

  setItem: (key, value) => {
    try {
      // Validate that value is serializable
      const serialized = JSON.stringify(value);

      // Check size limit (localStorage typically has 5MB limit)
      if (serialized.length > 5 * 1024 * 1024) {
        console.error('Data too large for localStorage');
        return false;
      }

      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      console.error(`Error writing to localStorage: ${key}`, error);
      return false;
    }
  },

  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing from localStorage: ${key}`, error);
      return false;
    }
  }
};

/**
 * Generate a simple session token (for demo purposes)
 * In production, this should come from the backend
 */
export const generateSessionToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Rate limiting for sensitive operations
 */
const rateLimitMap = new Map();
const RATE_LIMIT_CLEANUP_INTERVAL = 60000; // 1 minute
const RATE_LIMIT_MAX_ENTRIES = 1000;

// Cleanup old entries periodically to prevent memory leaks
let cleanupInterval = null;

const startCleanup = () => {
  if (cleanupInterval) return;

  cleanupInterval = setInterval(() => {
    const now = Date.now();
    const maxWindow = 5 * 60 * 1000; // 5 minutes max window

    for (const [key, attempts] of rateLimitMap.entries()) {
      const recentAttempts = attempts.filter(time => now - time < maxWindow);
      if (recentAttempts.length === 0) {
        rateLimitMap.delete(key);
      } else {
        rateLimitMap.set(key, recentAttempts);
      }
    }

    // If map grows too large, remove oldest entries
    if (rateLimitMap.size > RATE_LIMIT_MAX_ENTRIES) {
      const entries = Array.from(rateLimitMap.entries());
      entries.sort((a, b) => Math.min(...a[1]) - Math.min(...b[1]));
      const toRemove = entries.slice(0, entries.length - RATE_LIMIT_MAX_ENTRIES);
      toRemove.forEach(([key]) => rateLimitMap.delete(key));
    }
  }, RATE_LIMIT_CLEANUP_INTERVAL);
};

// Start cleanup on module load
startCleanup();

export const checkRateLimit = (key, maxAttempts = 5, windowMs = 60000) => {
  const now = Date.now();
  const attempts = rateLimitMap.get(key) || [];

  // Filter out old attempts
  const recentAttempts = attempts.filter(time => now - time < windowMs);

  if (recentAttempts.length >= maxAttempts) {
    return { allowed: false, retryAfter: windowMs - (now - recentAttempts[0]) };
  }

  recentAttempts.push(now);
  rateLimitMap.set(key, recentAttempts);

  return { allowed: true, retryAfter: 0 };
};

/**
 * Clear rate limit for a specific key (useful after successful auth)
 */
export const clearRateLimit = (key) => {
  rateLimitMap.delete(key);
};

/**
 * Stop the cleanup interval (useful for testing or app shutdown)
 */
export const stopRateLimitCleanup = () => {
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
  }
};
